import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/services/api';
import { EventOut, DrinkOut, FoodOut, DrinkTypeOut, FoodTypeOut, EventOccurrence, RecurrenceTimeWindow } from '@/types/api';
import { useCart } from '@/contexts/CartContext';
import { usePriceFormat } from '@/hooks/usePriceFormat';
import { useAuth } from '@/contexts/AuthContext';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Icon from '@/components/atoms/Icon/Icon';
import Loader from '@/components/atoms/Loader';
import RegistrationEncouragement from '@/components/molecules/RegistrationEncouragement';
import StockIndicator from '@/components/atoms/StockIndicator/StockIndicator';
import ScrollableCategories from '@/components/molecules/ScrollableCategories';
import { getStockErrorMessage } from '@/utils/errorHandling';
import { getProductImageProps } from '@/utils/imageUtils';
import './EventDetails.scss';

function mergeDuplicateProducts<T extends { name: string; type_id: number; stock_quantity: number; price: number; product_id?: number | null }>(items: T[]): T[] {
  const merged = new Map<string, T>();
  for (const item of items) {
    // Rows linked to the same Product already report the same live shared
    // stock (computed server-side) — dedupe by product_id and keep it as
    // reported, no summing (summing would multiply one shared quantity by
    // however many bars list it). Rows with no product_id are independent
    // (legacy, unmanaged) — fall back to the old name-based summing heuristic.
    const key = item.product_id != null ? `product:${item.product_id}` : `name:${item.name.trim().toLowerCase()}|${item.type_id}`;
    const existing = merged.get(key);
    if (existing) {
      if (item.product_id == null) {
        existing.stock_quantity += item.stock_quantity;
      }
      if (existing.price !== item.price) {
        console.warn(`Price mismatch merging "${item.name}": ${existing.price} vs ${item.price}`);
      }
    } else {
      merged.set(key, { ...item });
    }
  }
  return Array.from(merged.values());
}

const EventDetails: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { eventId, venueId } = useParams<{ eventId: string; venueId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, addToCart, updateCartItem, removeFromCart, setCurrentEventId, setCurrentVenueId } = useCart();
  const { formatWithTax } = usePriceFormat();
  const { isAuthenticated } = useAuth();
  
  const [event, setEvent] = useState<EventOut | null>(null);
  const [drinks, setDrinks] = useState<DrinkOut[]>([]);
  const [foods, setFoods] = useState<FoodOut[]>([]);
  const [drinkTypes, setDrinkTypes] = useState<DrinkTypeOut[]>([]);
  const [foodTypes, setFoodTypes] = useState<FoodTypeOut[]>([]);
  const [selectedDrinkType, setSelectedDrinkType] = useState<number | null>(null);
  const [selectedFoodType, setSelectedFoodType] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'drinks' | 'foods'>('drinks');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [addingToCart, setAddingToCart] = useState<{ [key: string]: boolean }>({});
  const [stockErrors, setStockErrors] = useState<{ [key: string]: string }>({});
  const [showRegistrationEncouragement, setShowRegistrationEncouragement] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventVenues, setEventVenues] = useState<any[]>([]);
  const [currentVenue, setCurrentVenue] = useState<any | null>(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const [occurrences, setOccurrences] = useState<EventOccurrence[]>([]);
  const [loadingOccurrences, setLoadingOccurrences] = useState(false);

  const [comboMixerSelection, setComboMixerSelection] = useState<Record<number, number | ''>>({});
  const [addingComboFor, setAddingComboFor] = useState<Record<number, boolean>>({});
  

  const parseLocalDateTime = useCallback((value: string) => {
    const normalized = value.replace(/Z$/, '').replace(/([+-]\d{2}:\d{2})$/, '');
    return new Date(normalized);
  }, []);

  const getWeekdayKey = useCallback((value: Date) => {
    return String((value.getDay() + 6) % 7);
  }, []);

  const getRecurrenceWindowForDate = useCallback((eventData: EventOut, date: Date) => {
    if (!eventData.recurrence_time_windows) return null;
    const dayKey = getWeekdayKey(date);
    const window = eventData.recurrence_time_windows[dayKey];
    if (!window || !window.start_time) return null;

    const [startHour, startMinute] = window.start_time.split(':').map(Number);
    const start = new Date(date);
    start.setHours(startHour || 0, startMinute || 0, 0, 0);

    let end: Date | null = null;
    if (window.end_time) {
      const [endHour, endMinute] = window.end_time.split(':').map(Number);
      end = new Date(start);
      end.setHours(endHour || 0, endMinute || 0, 0, 0);
      if (end <= start) {
        end.setDate(end.getDate() + 1);
      }
    } else if (window.duration_hours && window.duration_hours > 0) {
      end = new Date(start);
      end.setHours(end.getHours() + window.duration_hours);
    }

    return { start, end };
  }, [getWeekdayKey]);

  const getOccurrenceEnd = useCallback((
    startValue: string,
    endValue?: string | null,
    durationHours?: number | null,
    useEndDate: boolean = true
  ) => {
    const start = parseLocalDateTime(startValue);
    if (Number.isNaN(start.getTime())) return null;
    if (durationHours && durationHours > 0) {
      const end = new Date(start);
      end.setHours(end.getHours() + durationHours);
      return end;
    }
    if (useEndDate && endValue) {
      const parsedEnd = parseLocalDateTime(endValue);
      if (!Number.isNaN(parsedEnd.getTime())) {
        return parsedEnd;
      }
    }
    const end = new Date(start);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [parseLocalDateTime]);

  const upcomingOccurrences = React.useMemo(() => {
    const now = new Date();
    return occurrences
      .filter(occurrence => {
        const date = parseLocalDateTime(occurrence.start_date);
        return !Number.isNaN(date.getTime()) && date >= now;
      })
      .sort((a, b) => parseLocalDateTime(a.start_date).getTime() - parseLocalDateTime(b.start_date).getTime())
      .slice(0, 5);
  }, [occurrences, parseLocalDateTime]);

  // Reset registration encouragement when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      setShowRegistrationEncouragement(false);
    }
  }, [isAuthenticated]);

  const getErrorMessage = useCallback((err: any) => {
    // Check if it's a 404 error
    if (err.response?.status === 404) {
      return t('events.notFound');
    }
    return err.message || t('errors.general');
  }, [t]);

  const formatDateTime = useCallback((value?: string | null) => {
    if (!value) return t('events.eventCard.tbd');
    const parsed = parseLocalDateTime(value);
    if (Number.isNaN(parsed.getTime())) return t('events.eventCard.tbd');
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(parsed);
  }, [i18n.language, parseLocalDateTime, t]);

  const formatDateOnly = useCallback((value?: string | null) => {
    if (!value) return t('events.eventCard.tbd');
    const parsed = parseLocalDateTime(value);
    if (Number.isNaN(parsed.getTime())) return t('events.eventCard.tbd');
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium'
    }).format(parsed);
  }, [i18n.language, parseLocalDateTime, t]);

  const getRecurrenceDayLabel = useCallback((day: number) => {
    switch (day) {
      case 0:
        return t('events.recurrence.dayMon');
      case 1:
        return t('events.recurrence.dayTue');
      case 2:
        return t('events.recurrence.dayWed');
      case 3:
        return t('events.recurrence.dayThu');
      case 4:
        return t('events.recurrence.dayFri');
      case 5:
        return t('events.recurrence.daySat');
      case 6:
        return t('events.recurrence.daySun');
      default:
        return '';
    }
  }, [t]);

  const getRecurrenceFrequencyLabel = useCallback((frequency?: string | null) => {
    switch (frequency) {
      case 'daily':
        return t('events.recurrence.frequencyDaily');
      case 'weekly':
        return t('events.recurrence.frequencyWeekly');
      case 'monthly':
        return t('events.recurrence.frequencyMonthly');
      case 'yearly':
        return t('events.recurrence.frequencyYearly');
      default:
        return t('events.recurrence.frequencyUnknown');
    }
  }, [t]);

  const formatRecurrenceWindowLabel = useCallback((window: RecurrenceTimeWindow) => {
    if (window.end_time) {
      return `${window.start_time}–${window.end_time}`;
    }
    if (window.duration_hours && window.duration_hours > 0) {
      return `${window.start_time} (+${window.duration_hours}h)`;
    }
    return window.start_time;
  }, []);

  const toDateKey = useCallback((value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const startOfWeekMonday = useCallback((value: Date) => {
    const dayIndex = (value.getDay() + 6) % 7;
    const start = new Date(value);
    start.setDate(value.getDate() - dayIndex);
    start.setHours(0, 0, 0, 0);
    return start;
  }, []);

  const buildFallbackOccurrences = useCallback((eventData: EventOut, startDate: Date, count: number) => {
    const eventStart = eventData.start_date ? parseLocalDateTime(eventData.start_date) : null;
    if (!eventStart || Number.isNaN(eventStart.getTime())) {
      return [];
    }

    const interval = Math.max(1, eventData.recurrence_interval ?? 1);
    const exceptions = new Set((eventData.recurrence_exceptions || []).map(value => value.trim()));
    const results: EventOccurrence[] = [];
    const baseDate = new Date(startDate);

    if (eventData.recurrence_frequency === 'weekly') {
      const allowedDays = (eventData.recurrence_days || []).slice().sort((a, b) => a - b);
      if (allowedDays.length === 0) return [];
      const eventWeekStart = startOfWeekMonday(eventStart);
      const maxIterations = 366 * 3;

      for (let i = 0; i < maxIterations && results.length < count; i += 1) {
        const candidate = new Date(baseDate);
        candidate.setDate(baseDate.getDate() + i);
        const candidateDay = (candidate.getDay() + 6) % 7;
        if (!allowedDays.includes(candidateDay)) continue;
        const candidateWeekStart = startOfWeekMonday(candidate);
        const weekDiff = Math.floor(
          (candidateWeekStart.getTime() - eventWeekStart.getTime()) / (7 * 24 * 60 * 60 * 1000)
        );
        if (weekDiff < 0 || weekDiff % interval !== 0) continue;
        const key = toDateKey(candidate);
        if (exceptions.has(key)) continue;
        const window = getRecurrenceWindowForDate(eventData, candidate);
        const start = window?.start
          ? window.start
          : (() => {
              const fallbackStart = new Date(candidate);
              fallbackStart.setHours(eventStart.getHours(), eventStart.getMinutes(), 0, 0);
              return fallbackStart;
            })();
        const end = window?.end ?? getOccurrenceEnd(
          start.toISOString(),
          eventData.end_date,
          eventData.duration_hours ?? null,
          false
        );
        results.push({
          event_id: eventData.id,
          start_date: start.toISOString(),
          end_date: end ? end.toISOString() : null
        });
      }
    }

    return results;
  }, [getOccurrenceEnd, getRecurrenceWindowForDate, parseLocalDateTime, startOfWeekMonday, toDateKey]);

  const activeOccurrence = React.useMemo(() => {
    if (!event) return null;
    const now = new Date();
    const selectedOccurrenceParam = new URLSearchParams(location.search).get('occurrence');

    if (selectedOccurrenceParam) {
      const start = parseLocalDateTime(selectedOccurrenceParam);
      const window = getRecurrenceWindowForDate(event, start);
      const end = window?.end ?? getOccurrenceEnd(
        selectedOccurrenceParam,
        event.end_date,
        event.duration_hours ?? null,
        false
      );
      if (!Number.isNaN(start.getTime()) && end && now >= start && now <= end) {
        return { event_id: event.id, start_date: selectedOccurrenceParam, end_date: event.end_date ?? null };
      }
    }

    if (event.is_recurring) {
      for (const occurrence of occurrences) {
        const start = parseLocalDateTime(occurrence.start_date);
        if (Number.isNaN(start.getTime())) continue;
        const window = getRecurrenceWindowForDate(event, start);
        const end = occurrence.end_date
          ? parseLocalDateTime(occurrence.end_date)
          : (window?.end ?? getOccurrenceEnd(
              occurrence.start_date,
              event.end_date,
              event.duration_hours ?? null,
              false
            ));
        if (!end) continue;
        if (now >= start && now <= end) {
          return occurrence;
        }
      }
      const fallbackStartDate = (() => {
        const buffered = new Date(now);
        const durationHours = event.duration_hours ?? 0;
        if (durationHours > 0) {
          buffered.setHours(buffered.getHours() - durationHours);
        }
        return buffered;
      })();
      const fallbackOccurrences = buildFallbackOccurrences(event, fallbackStartDate, 14);
      for (const occurrence of fallbackOccurrences) {
        const start = parseLocalDateTime(occurrence.start_date);
        if (Number.isNaN(start.getTime())) continue;
        const window = getRecurrenceWindowForDate(event, start);
        const end = occurrence.end_date
          ? parseLocalDateTime(occurrence.end_date)
          : (window?.end ?? getOccurrenceEnd(
              occurrence.start_date,
              event.end_date,
              event.duration_hours ?? null,
              false
            ));
        if (!end) continue;
        if (now >= start && now <= end) {
          return occurrence;
        }
      }
      return null;
    }

    if (event.start_date) {
      const start = parseLocalDateTime(event.start_date);
      if (Number.isNaN(start.getTime())) return null;
      const end = getOccurrenceEnd(event.start_date, event.end_date, event.duration_hours ?? null, true);
      if (!end) return null;
      return now >= start && now <= end ? { event_id: event.id, start_date: event.start_date, end_date: event.end_date } : null;
    }

    return null;
  }, [buildFallbackOccurrences, event, getOccurrenceEnd, getRecurrenceWindowForDate, location.search, occurrences, parseLocalDateTime]);

  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventId || !venueId) return;
      
      try {
        setLoading(true);
        
        // Set current event ID for cart operations
        setCurrentEventId(parseInt(eventId));
        setCurrentVenueId(parseInt(venueId));
        
        // Fetch event details and venues
        const [eventData, venuesData] = await Promise.all([
          apiService.getEvent(parseInt(eventId)),
          apiService.getAnonymousEventVenues(parseInt(eventId))
        ]);
        
        setEvent(eventData);
        setEventVenues(venuesData);
        
        // Find the current venue if venueId is not 0
        if (parseInt(venueId) !== 0) {
          const venue = venuesData.find(v => v.id === parseInt(venueId));
          setCurrentVenue(venue || null);
        } else {
          setCurrentVenue(null);
        }
        
        if (eventData.is_recurring) {
          let fallbackStartDate = new Date();
          try {
            setLoadingOccurrences(true);
            const now = new Date();
            const parsedEventStart = eventData.start_date ? parseLocalDateTime(eventData.start_date) : null;
            const eventStart =
              parsedEventStart && !Number.isNaN(parsedEventStart.getTime())
                ? parsedEventStart
                : null;
            const durationHours = eventData.duration_hours ?? 0;
            const startDate = eventStart && eventStart > now
              ? eventStart
              : (() => {
                  const buffered = new Date(now);
                  if (durationHours > 0) {
                    buffered.setHours(buffered.getHours() - durationHours);
                  }
                  return buffered;
                })();
            fallbackStartDate = startDate;
            const endDate = new Date(startDate);
            const interval = eventData.recurrence_interval ?? 1;
            switch (eventData.recurrence_frequency) {
              case 'daily':
                endDate.setDate(endDate.getDate() + Math.max(1, interval * 2));
                break;
              case 'weekly':
                endDate.setDate(endDate.getDate() + Math.max(7, interval * 14));
                break;
              case 'monthly':
                endDate.setMonth(endDate.getMonth() + Math.max(1, interval * 2));
                break;
              case 'yearly':
                endDate.setFullYear(endDate.getFullYear() + Math.max(1, interval * 2));
                break;
              default:
                endDate.setMonth(endDate.getMonth() + 6);
                break;
            }
            const occurrenceData = await apiService.getEventOccurrences(eventData.id, {
              start_date: startDate.toISOString(),
              end_date: endDate.toISOString()
            });
            const normalized = Array.isArray(occurrenceData) ? occurrenceData : [];
            if (normalized.length > 0) {
              setOccurrences(normalized);
            } else {
              setOccurrences(buildFallbackOccurrences(eventData, startDate, 5));
            }
          } catch (err) {
            console.warn('Failed to load occurrences:', err);
            setOccurrences(buildFallbackOccurrences(eventData, fallbackStartDate, 5));
          } finally {
            setLoadingOccurrences(false);
          }
        } else {
          setOccurrences([]);
        }

        // Fetch drinks, foods, and their types
        // If venueId is 0, use the original event-wide endpoints (no venue filtering)
        // Otherwise, use venue-specific endpoints
        const isDefaultVenue = parseInt(venueId) === 0;
        
        const [drinksData, foodsData, drinkTypesData, foodTypesData] = await Promise.all([
          isDefaultVenue 
            ? apiService.getAnonymousEventDrinks(parseInt(eventId), { excludeVip: true })
            : apiService.getAnonymousEventDrinksByVenue(parseInt(eventId), parseInt(venueId)),
          isDefaultVenue 
            ? apiService.getAnonymousEventFoods(parseInt(eventId), { excludeVip: true })
            : apiService.getAnonymousEventFoodsByVenue(parseInt(eventId), parseInt(venueId)),
          apiService.getDrinkTypes(),
          apiService.getFoodTypes()
        ]);
        
        const mergedDrinks = isDefaultVenue ? mergeDuplicateProducts(drinksData) : drinksData;
        const mergedFoods = isDefaultVenue ? mergeDuplicateProducts(foodsData) : foodsData;

        setDrinks(mergedDrinks);
        setFoods(mergedFoods);
        setDrinkTypes(drinkTypesData);
        setFoodTypes(foodTypesData);

        if (mergedDrinks.length > 0) {
          setActiveTab('drinks');
        } else if (mergedFoods.length > 0) {
          setActiveTab('foods');
        }
        
      } catch (err: any) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventId, venueId, getErrorMessage, setCurrentEventId]);

  const handleQuantityChange = useCallback((itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    setQuantities(prev => ({
      ...prev,
      [itemId]: newQuantity
    }));
  }, []);

  const handleAddToCart = useCallback(async (item: DrinkOut | FoodOut, type: 'drink' | 'food') => {
    const itemId = `${type}-${item.id}`;
    const quantity = quantities[itemId] || 1;
    
    // Check if item is available and has stock
    if (!item.is_available || item.stock_quantity <= 0) {
      return;
    }
    
    setAddingToCart(prev => ({ ...prev, [itemId]: true }));
    setStockErrors(prev => ({ ...prev, [itemId]: '' })); // Clear previous errors
    
    try {
      const stockError = await addToCart({
        quantity: quantity,
        ...(type === 'drink' ? { drink_id: item.id } : { food_id: item.id })
      });
      
      if (stockError) {
        // Show stock error message
        const errorMessage = getStockErrorMessage(stockError, t);
        setStockErrors(prev => ({ ...prev, [itemId]: errorMessage }));
      } else {
        // Success - reset quantity after adding to cart
        setQuantities(prev => ({ ...prev, [itemId]: 1 }));
      }
    } catch (err: any) {
      console.error('Error adding to cart:', err);
    } finally {
      setAddingToCart(prev => ({ ...prev, [itemId]: false }));
    }
  }, [addToCart, quantities, t]);

  const handleSelectMixerForBase = useCallback(async (baseDrink: DrinkOut, mixerId: number) => {
    setAddingComboFor(prev => ({ ...prev, [baseDrink.id]: true }));
    const groupId = crypto.randomUUID();
    const itemKey = `drink-${baseDrink.id}`;

    try {
      const baseError = await addToCart({ quantity: 1, drink_id: baseDrink.id, combo_group_id: groupId });
      if (baseError) {
        setStockErrors(prev => ({ ...prev, [itemKey]: getStockErrorMessage(baseError, t) }));
        return;
      }
      const mixerError = await addToCart({ quantity: 1, drink_id: mixerId, combo_group_id: groupId });
      if (mixerError) {
        setStockErrors(prev => ({ ...prev, [itemKey]: getStockErrorMessage(mixerError, t) }));
        return;
      }
      setStockErrors(prev => ({ ...prev, [itemKey]: '' }));
    } catch (err) {
      console.error('Error adding combo to cart:', err);
    } finally {
      setComboMixerSelection(prev => ({ ...prev, [baseDrink.id]: '' }));
      setAddingComboFor(prev => ({ ...prev, [baseDrink.id]: false }));
    }
  }, [addToCart, t]);

  const getCartItemQuantity = (itemId: number, type: 'drink' | 'food') => {
    if (!cart?.items) return 0;
    const item = cart.items.find(item => 
      (type === 'drink' ? item.drink_id === itemId : item.food_id === itemId) && 
      item.item_type === type
    );
    return item?.quantity || 0;
  };

  const handleIncreaseQuantity = useCallback(async (itemId: number, type: 'drink' | 'food') => {
    const currentQuantity = getCartItemQuantity(itemId, type);
    // Encontrar el item en el carrito para obtener su ID real
    const cartItem = cart?.items.find(item => 
      (type === 'drink' ? item.drink_id === itemId : item.food_id === itemId) && 
      item.item_type === type
    );
    
    if (cartItem) {
      await updateCartItem(cartItem.id, currentQuantity + 1);
    }
  }, [cart, updateCartItem, getCartItemQuantity]);

  const handleDecreaseQuantity = useCallback(async (itemId: number, type: 'drink' | 'food') => {
    const currentQuantity = getCartItemQuantity(itemId, type);
    // Encontrar el item en el carrito para obtener su ID real
    const cartItem = cart?.items.find(item => 
      (type === 'drink' ? item.drink_id === itemId : item.food_id === itemId) && 
      item.item_type === type
    );
    
    if (cartItem && currentQuantity > 1) {
      await updateCartItem(cartItem.id, currentQuantity - 1);
    }
  }, [cart, updateCartItem, getCartItemQuantity]);

  const handleRemoveFromCart = useCallback(async (itemId: number, type: 'drink' | 'food') => {
    // Encontrar el item en el carrito para obtener su ID real
    const cartItem = cart?.items.find(item => 
      (type === 'drink' ? item.drink_id === itemId : item.food_id === itemId) && 
      item.item_type === type
    );
    
    if (cartItem) {
      await removeFromCart(cartItem.id);
    }
  }, [cart, removeFromCart]);

  const toggleDescription = useCallback((itemId: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Determine back navigation path based on whether event has venues
  // const getBackNavigationPath = useCallback(() => {
  //   if (eventVenues.length > 0) {
  //     // If event has venues, go back to venues list
  //     return `/events/${eventId}`;
  //   } else {
  //     // If event has no venues, go back to events list
  //     return '/events';
  //   }
  // }, [eventVenues.length, eventId]);

  const getBackNavigationPath = useCallback(() => {
    return '/events';
  }, []);

  // Filter types to only show those that have items
  const availableDrinkTypes = drinkTypes.filter(type => 
    type.combo_role !== 'mixer' && drinks.some(drink => drink.type_id === type.id)
  );

  const availableFoodTypes = foodTypes.filter(type => 
    foods.some(food => food.type_id === type.id)
  );

  // Filter functions - out-of-stock products are hidden entirely, not just marked sold-out
  const filteredDrinks = drinks.filter(drink => {
    // Type filter
    const matchesType = !selectedDrinkType || drink.type_id === selectedDrinkType;
    // Search filter
    const matchesSearch = !searchTerm || drink.name.toLowerCase().includes(searchTerm.toLowerCase());
    // Hide standalone mixers and recipe-only ingredients — never purchasable on their own
    const drinkType = drinkTypes.find(type => type.id === drink.type_id);
    const isHiddenFromMenu = drinkType?.combo_role === 'mixer' || drinkType?.combo_role === 'ingredient';
    const inStock = drink.is_available && drink.stock_quantity > 0;
    return matchesType && matchesSearch && !isHiddenFromMenu && inStock;
  });

  const sortedDrinks = [...filteredDrinks].sort((a, b) => {
    if (a.type_id !== b.type_id) {
      return (a.type_id || 0) - (b.type_id || 0);
    }
    return a.name.localeCompare(b.name);
  });

  const filteredFoods = foods.filter(food => {
    // Type filter
    const matchesType = !selectedFoodType || food.type_id === selectedFoodType;
    // Search filter
    const matchesSearch = !searchTerm || food.name.toLowerCase().includes(searchTerm.toLowerCase());
    const inStock = food.is_available && food.stock_quantity > 0;
    return matchesType && matchesSearch && inStock;
  });

  const sortedFoods = [...filteredFoods].sort((a, b) => {
    if (a.type_id !== b.type_id) {
      return (a.type_id || 0) - (b.type_id || 0);
    }
    return a.name.localeCompare(b.name);
  });

  const comboMixerDrinks = drinks.filter(d => {
    const type = drinkTypes.find(t => t.id === d.type_id);
    return type?.combo_role === 'mixer' && d.is_available && d.stock_quantity > 0;
  });

  if (loading) {
    return <Loader fullScreen size="lg" />;
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <p>{error}</p>
          <Button 
            variant="primary" 
            onClick={() => navigate(-1)}
          >
            {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container">
        <div className="error">
          <p>{t('events.notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="event-details-page">
        <div className="event-details-page__header">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(getBackNavigationPath())}
          >
            <Icon name="atras" size={20}  />  
          </Button>
          <h1 className="event-details-page__title">
            {currentVenue ? currentVenue.name : (event?.name || t('common.loading'))}
          </h1>
        </div>

        <div className="event-details-page__content">
          {!activeOccurrence && (
            <Card variant="elevated" padding="md" className="event-details-page__inactive">
              <h3 className="event-details-page__inactive-title">
                {t('events.recurrence.notActiveTitle')}
              </h3>
              <p className="event-details-page__inactive-text">
                {t('events.recurrence.notActiveBody')}
              </p>
              {upcomingOccurrences.length > 0 && (
                <p className="event-details-page__inactive-next">
                  {t('events.recurrence.nextOccurrenceAt', {
                    date: formatDateTime(upcomingOccurrences[0].start_date)
                  })}
                </p>
              )}
              <Button variant="secondary" size="sm" onClick={() => navigate(getBackNavigationPath())}>
                {t('common.back')}
              </Button>
            </Card>
          )}
          {/* <div className="event-details-page__info">
            <div className="event-details-page__image">
              {event.logo && (
                <img src={event.logo} alt={event.name} />
              )}
            </div>
            <div className="event-details-page__details">
              <h2 className="event-details-page__subtitle">{event.description}</h2>
              <p className="event-details-page__date">
                {event.start_date && new Date(event.start_date).toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="event-details-page__location">
                📍 {event.address}, {event.city}, {event.state}
              </p>
            </div>
          </div> */}

          <div className="event-details-page__summary">
            <Card variant="elevated" padding="md" className="event-details-page__summary-card">
              <div className="event-details-page__summary-row">
                <span className="event-details-page__summary-label">{t('events.eventCard.date')}</span>
                <span className="event-details-page__summary-value">
                  {(() => {
                    if (!event.start_date) return formatDateTime(event.start_date);
                    const startDate = parseLocalDateTime(event.start_date);
                    const window = event.is_recurring ? getRecurrenceWindowForDate(event, startDate) : null;
                    const startValue = window?.start ? window.start.toISOString() : event.start_date;
                    const end = window?.end ?? getOccurrenceEnd(
                      startValue,
                      event.end_date,
                      event.duration_hours ?? null,
                      !event.is_recurring
                    );
                    return end
                      ? `${formatDateTime(startValue)} - ${formatDateTime(end.toISOString())}`
                      : formatDateTime(startValue);
                  })()}
                </span>
              </div>
              <div className="event-details-page__summary-row">
                <span className="event-details-page__summary-label">{t('events.eventCard.location')}</span>
                <span className="event-details-page__summary-value">
                  {event.address || `${event.city || ''}${event.city && event.state ? ', ' : ''}${event.state || ''}`}
                </span>
              </div>
              {event.is_recurring && (
                <>
                  <div className="event-details-page__summary-row">
                    <span className="event-details-page__summary-label">{t('events.recurrence.frequencyLabel')}</span>
                    <span className="event-details-page__summary-value">
                      {getRecurrenceFrequencyLabel(event.recurrence_frequency)}
                    </span>
                  </div>
                  <div className="event-details-page__summary-row">
                    <span className="event-details-page__summary-label">{t('events.recurrence.intervalLabel')}</span>
                    <span className="event-details-page__summary-value">
                      {event.recurrence_interval ?? 1}
                    </span>
                  </div>
                  {event.recurrence_frequency === 'weekly' && (
                    <div className="event-details-page__summary-row">
                      <span className="event-details-page__summary-label">{t('events.recurrence.daysLabel')}</span>
                      <span className="event-details-page__summary-value">
                        {(event.recurrence_days || []).map(getRecurrenceDayLabel).filter(Boolean).join(', ') || t('events.recurrence.none')}
                      </span>
                    </div>
                  )}
                  {event.recurrence_frequency === 'weekly' && event.recurrence_time_windows && (
                    <div className="event-details-page__summary-row">
                      <span className="event-details-page__summary-label">{t('events.recurrence.timeWindowsLabel')}</span>
                      <span className="event-details-page__summary-value">
                        {Object.entries(event.recurrence_time_windows)
                          .sort(([a], [b]) => Number(a) - Number(b))
                          .map(([day, window]) => `${getRecurrenceDayLabel(Number(day))}: ${formatRecurrenceWindowLabel(window)}`)
                          .join(', ')}
                      </span>
                    </div>
                  )}
                  {event.recurrence_exceptions && event.recurrence_exceptions.length > 0 && (
                    <div className="event-details-page__summary-row">
                      <span className="event-details-page__summary-label">{t('events.recurrence.exceptionsLabel')}</span>
                      <span className="event-details-page__summary-value">
                        {event.recurrence_exceptions.map(date => formatDateOnly(date)).join(', ')}
                      </span>
                    </div>
                  )}
                </>
              )}
            </Card>

            {event.is_recurring && (
              <Card variant="elevated" padding="md" className="event-details-page__occurrences-card">
                <h3 className="event-details-page__occurrences-title">
                  {t('events.recurrence.nextOccurrences')}
                </h3>
                {loadingOccurrences ? (
                  <p className="event-details-page__occurrences-empty">
                    {t('events.recurrence.loadingOccurrences')}
                  </p>
                ) : upcomingOccurrences.length === 0 ? (
                  <p className="event-details-page__occurrences-empty">
                    {t('events.recurrence.noUpcomingOccurrences')}
                  </p>
                ) : (
                  <ul className="event-details-page__occurrences-list">
                    {upcomingOccurrences.map(occurrence => (
                      <li key={occurrence.start_date} className="event-details-page__occurrences-item">
                        {formatDateTime(occurrence.start_date)}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            )}
          </div>

          {/* Menu Tabs */}
          {activeOccurrence && (drinks.length > 0 || foods.length > 0) && (
            <div className="event-details-page__menu">
              {/* Tab Navigation - Only show when both drinks and foods are available */}
              {drinks.length > 0 && foods.length > 0 && (
                <div className="event-details-page__tabs">
                  <Button
                    variant={activeTab === 'drinks' ? 'primary' : 'outline'}
                    size="md"
                    onClick={() => {
                      setActiveTab('drinks');
                      setSelectedDrinkType(null); // Reset drink filter when switching to drinks tab
                    }}
                    className="tab-button"
                  >
                    <Icon name="vasos" size={20}  />  {t('drinks.title')}
                  </Button>
                  <Button
                    variant={activeTab === 'foods' ? 'primary' : 'outline'}
                    size="md"
                    onClick={() => {
                      setActiveTab('foods');
                      setSelectedFoodType(null); // Reset food filter when switching to foods tab
                    }}
                    className="tab-button"
                  >
                    <Icon name="cubiertos" size={20}  />  {t('foods.title')}
                  </Button>
                </div>
              )}

              {/* Search Bar */}
              <div className="event-details-page__search">
                <div className="event-details-page__search-container">
                  <input
                    type="text"
                    placeholder={t('menu.search.placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="event-details-page__search-input"
                  />
                  <Icon className="event-details-page__search-icon" name="lupa" size={20} alt={t('menu.search.placeholder')} />
                </div>
              </div>

              {/* Tab Content */}
              <div className="event-details-page__tab-content">

                {/* Drinks Tab */}
                {activeTab === 'drinks' && drinks.length > 0 && (
                  <div className="event-details-page__section">
                    {/* Drink Type Filters - Scrollable Categories */}
                    {availableDrinkTypes.length > 0 && (
                      <ScrollableCategories
                        categories={[
                          { id: null, name: t('drinks.allTypes') },
                          ...availableDrinkTypes.map(type => ({ id: type.id, name: type.name }))
                        ]}
                        selectedCategory={selectedDrinkType}
                        onCategorySelect={setSelectedDrinkType}
                        className="event-details-page__filters"
                      />
                    )}
                    
                    <div className="event-details-page__items-grid">
                      {sortedDrinks.map((drink, index) => {
                          const itemId = `drink-${drink.id}`;
                          const cartQuantity = getCartItemQuantity(drink.id, 'drink');
                          const isSoldOut = !drink.is_available || drink.stock_quantity <= 0;
                          const drinkType = drinkTypes.find(type => type.id === drink.type_id);
                          const drinkTypeName = drinkType?.name || t('drinks.allTypes');
                          
                          // Check if this is the first drink or if the type has changed
                          const previousDrink = index > 0 ? sortedDrinks[index - 1] : null;
                          const previousDrinkType = previousDrink 
                            ? drinkTypes.find(type => type.id === previousDrink.type_id)
                            : null;
                          const showTypeTitle = index === 0 || previousDrinkType?.id !== drinkType?.id;
                        
                        return (
                          <>
                            {showTypeTitle && drinkTypeName && <h2 className="item-card__type-title"> {drinkTypeName} </h2> }
                            <Card key={drink.id} variant="elevated" className={`item-card ${isSoldOut ? 'item-card--sold-out' : ''}`}>
                              <div className="item-card__content">
                                {/* Product Image */}
                                <div className="item-card__info">
                                  <div className="item-card__image">
                                    
                                    {drink.image_url === null || drink.image_url?.includes('example.com') ? (
                                      <img 
                                        className="item-card__image-img"
                                        {...getProductImageProps(drink.name, 'drink', drink.image_url)}
                                        alt={drink.name}
                                      />
                                    ) : (
                                      <img 
                                        className="item-card__image-img"
                                        src={drink.image_url}
                                        alt={drink.name}
                                      />
                                    )}
                                  </div>
                                  <div className="item-card__detalles">
                                    <div className="item-card__title-container">
                                      <h3 className="item-card__title">{drink.name}</h3>
                                      {/* Stock Indicator */}
                                      <StockIndicator
                                        stockQuantity={drink.stock_quantity}
                                        maxStock={drink.max_stock}
                                        minStockThreshold={drink.min_stock_threshold}
                                        isAvailable={drink.is_available}
                                        showDetails={false}
                                      />
                                    </div>
                                    {drink.description && (
                                      <div className={`item-card__description-wrapper ${expandedDescriptions.has(`drink-${drink.id}`) ? 'expanded' : ''}`}>
                                        <p className="item-card__description">
                                        {expandedDescriptions.has(`drink-${drink.id}`) ? 
                                          drink.description : 
                                          (drink.description && drink.description.length > 50 
                                            ? (() => {
                                                const truncated = drink.description.substring(0, 50);
                                                const lastSpaceIndex = truncated.lastIndexOf(' ');
                                                return lastSpaceIndex > 0 ? `${truncated.substring(0, lastSpaceIndex)}...` : `${truncated}...`;
                                             })()
                                            : drink.description)}
                                          {drink.description && drink.description.length > 50 && (
                                          <a
                                            href="#"
                                            className="item-card__toggle-description"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              toggleDescription(`drink-${drink.id}`);
                                            }}
                                            title={
                                              expandedDescriptions.has(`drink-${drink.id}`)
                                                ? t('events.eventCard.viewLess')
                                                : t('events.eventCard.viewMore')
                                            }
                                          >
                                            <span className="item-card__toggle-description-text">
                                              {expandedDescriptions.has(`drink-${drink.id}`)
                                                ? (
                                                    <>
                                                      {t('events.eventCard.viewLess')}{' '}
                                                      <Icon name="arriba" size={16} alt={t('events.eventCard.viewLess')} />
                                                    </>
                                                  )
                                                : (
                                                    <>
                                                      {t('events.eventCard.viewMore')}{' '}
                                                      <Icon name="abajo" size={16} alt={t('events.eventCard.viewMore')} />
                                                    </>
                                                  )
                                              }
                                            </span>
                                          </a>
                                        )}
                                        </p>
                                        {/* Show toggle if description is likely longer than 2 lines (~100 chars) */}
                                        
                                      </div>
                                    )}
                                  <p className="item-card__price">{formatWithTax(drink.price, drink.tax_rate)}</p>
                                  {drinkType?.combo_role === 'base' && comboMixerDrinks.length > 0 && (
                                    <select
                                      value={comboMixerSelection[drink.id] ?? ''}
                                      disabled={addingComboFor[drink.id]}
                                      onChange={(e) => {
                                        const mixerId = e.target.value ? Number(e.target.value) : '';
                                        setComboMixerSelection(prev => ({ ...prev, [drink.id]: mixerId }));
                                        if (mixerId) {
                                          handleSelectMixerForBase(drink, mixerId as number);
                                        }
                                      }}
                                      className="item-card__mixer-select"
                                    >
                                      <option value="">{t('drinks.selectMixer', 'Sin mixer')}</option>
                                      {comboMixerDrinks.map(m => (
                                        <option key={m.id} value={m.id}>{m.name}</option>
                                      ))}
                                    </select>
                                  )}
                                  </div>
                                  <div className="item-card__actions">
                                  {/* Only show cart controls if product is available and in cart */}
                                  {!isSoldOut && cartQuantity > 0 && (
                                  <div className="item-card__quantity-controls">
                                    {cartQuantity === 1 && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRemoveFromCart(drink.id, 'drink')}
                                      className="item-card__remove-btn"
                                    >
                                      <Icon name="basurero" size={20} />
                                    </Button>
                                    )}
                                    {cartQuantity > 1 && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDecreaseQuantity(drink.id, 'drink')}
                                      disabled={cartQuantity <= 1}
                                      className='item-card__remove-btn'
                                    >
                                      <Icon name="abajo" size={20} />
                                    </Button>
                                    )}
                                    <span className="item-card__quantity-display">
                                      {cartQuantity} {/* = {formatPrice(drink.price * cartQuantity)} */}
                                    </span>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleIncreaseQuantity(drink.id, 'drink')}
                                      className='item-card__remove-btn'
                                    >
                                      <Icon name="arriba" size={20} />
                                    </Button>
                                  </div>
                                  )}

                                  {/* Show add to cart button only if product is available */}
                                  {!isSoldOut && cartQuantity === 0 && (
                                  <Button
                                    className='item-card__agregar'
                                    variant="primary"
                                    size="sm"
                                    fullWidth
                                    onClick={() => handleAddToCart(drink, 'drink')}
                                    loading={addingToCart[itemId]}
                                    disabled={addingToCart[itemId]}
                                  >
                                    <Icon name="carrito" size={20} alt={t('cart.title')} />
                                    {addingToCart[itemId] ? t('cart.adding') : t('cart.addToCart')}

                                  </Button>
                                  )}

                                  {/* Show sold out message if product is not available */}
                                  {isSoldOut && (
                                    <div className="item-card__sold-out-message">
                                      <span className="item-card__sold-out-text">{t('stock.outOfStock')}</span>
                                    </div>
                                  )}

                                  {/* Stock Error Message */}
                                  {stockErrors[itemId] && (
                                  <div className="item-card__error">
                                    {stockErrors[itemId]}
                                  </div>
                                  )}
                                </div>
                              </div>
                              </div>
                            </Card>
                          </>
                        );
                      })
                      }
                      
                    </div>
                  </div>
                )}






                {/* Foods Tab */}
                {activeTab === 'foods' && foods.length > 0 && (
                  <div className="event-details-page__section">
                    {/* Food Type Filters - Scrollable Categories */}
                    {availableFoodTypes.length > 0 && (
                      <ScrollableCategories
                        categories={[
                          { id: null, name: t('foods.allTypes') },
                          ...availableFoodTypes.map(type => ({ id: type.id, name: type.name }))
                        ]}
                        selectedCategory={selectedFoodType}
                        onCategorySelect={setSelectedFoodType}
                        className="event-details-page__filters"
                      />
                    )}
                    
                    <div className="event-details-page__items-grid">
                      {sortedFoods.map((food, index) => {
                        const itemId = `food-${food.id}`;
                        const cartQuantity = getCartItemQuantity(food.id, 'food');
                        const isSoldOut = !food.is_available || food.stock_quantity <= 0;
                        const foodType = foodTypes.find(type => type.id === food.type_id);
                        const foodTypeName = foodType?.name || t('foods.allTypes');
                        const previousFood = index > 0 ? sortedFoods[index - 1] : null;
                        const previousFoodType = previousFood ? foodTypes.find(type => type.id === previousFood.type_id) : null;
                        const showTypeTitle = index === 0 || previousFoodType?.id !== foodType?.id;
                        
                        return (
                          <>
                            {showTypeTitle && foodTypeName && <h2 className="item-card__type-title"> {foodTypeName} </h2> }
                            <Card key={food.id} variant="elevated" className={`item-card ${isSoldOut ? 'item-card--sold-out' : ''}`}>
                              <div className="item-card__content">
                                {/* Product Image */}
                                <div className="item-card__info">
                                  <div className="item-card__image">
                                    {food.image_url === null || food.image_url?.includes('example.com') ? (
                                      <img 
                                        className="item-card__image-img"
                                        {...getProductImageProps(food.name, 'food', food.image_url)}
                                        alt={food.name}
                                      />
                                    ) : (
                                      <img 
                                        className="item-card__image-img"
                                        src={food.image_url}
                                        alt={food.name}
                                      />
                                    )}
                                    {isSoldOut && (
                                      <div className="item-card__sold-out-overlay">
                                        <span className="item-card__sold-out-text">{t('stock.outOfStock')}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="item-card__detalles">
                                    <h3 className="item-card__title">{food.name}</h3>
                                    {food.description && (
                                      <div className={`item-card__description-wrapper ${expandedDescriptions.has(`food-${food.id}`) ? 'expanded' : ''}`}>
                                        <p className="item-card__description">
                                          {expandedDescriptions.has(`food-${food.id}`) ? 
                                          food.description : 
                                          (food.description && food.description.length > 50 
                                            ? (() => {
                                                const truncated = food.description.substring(0, 50);
                                                const lastSpaceIndex = truncated.lastIndexOf(' ');
                                                return lastSpaceIndex > 0 ? `${truncated.substring(0, lastSpaceIndex)}...` : `${truncated}...`;
                                             })()
                                            : food.description)}
                                          {food.description && food.description.length > 50 && (
                                            <a
                                              href="#"
                                              className="item-card__toggle-description"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                toggleDescription(`food-${food.id}`);
                                              }}
                                              title={
                                                expandedDescriptions.has(`food-${food.id}`)
                                                  ? t('events.eventCard.viewLess')
                                                  : t('events.eventCard.viewMore')
                                              }
                                            >
                                              <span className="item-card__toggle-description-text">
                                                {expandedDescriptions.has(`food-${food.id}`)
                                                  ? (
                                                      <>
                                                        {t('events.eventCard.viewLess')}{' '}
                                                        <Icon name="arriba" size={16} alt={t('events.eventCard.viewLess')} />
                                                      </>
                                                    )
                                                  : (
                                                      <>
                                                        {t('events.eventCard.viewMore')}{' '}
                                                        <Icon name="abajo" size={16} alt={t('events.eventCard.viewMore')} />
                                                      </>
                                                    )
                                                }
                                              </span>
                                            </a>
                                          )}
                                        </p>
                                        
                                      </div>
                                    )}
                                    <p className="item-card__price">{formatWithTax(food.price, food.tax_rate)}</p>
                                
                                    {/* Stock Indicator */}
                                    <StockIndicator
                                      stockQuantity={food.stock_quantity}
                                      maxStock={food.max_stock}
                                      minStockThreshold={food.min_stock_threshold}
                                      isAvailable={food.is_available}
                                      showDetails={false}
                                    />
                                
                                    {/*food.allergens && (
                                      <p className="item-card__allergens">
                                        {t('foods.allergens')}: {food.allergens}
                                      </p>
                                    )*/}
                                  </div>
                                  <div className="item-card__actions">
                                    {/* Only show cart controls if product is available and in cart */}
                                    {!isSoldOut && cartQuantity > 0 && (
                                    <div className="item-card__quantity-controls">
                                      {cartQuantity === 1 && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleRemoveFromCart(food.id, 'food')}
                                        className="item-card__remove-btn"
                                      >
                                        <Icon name="basurero" size={20} />
                                      </Button>
                                      )}
                                      {cartQuantity > 1 && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDecreaseQuantity(food.id, 'food')}
                                        disabled={cartQuantity <= 1}
                                        className='item-card__remove-btn'
                                      >
                                        <Icon name="abajo" size={20} />
                                      </Button>
                                      )}
                                      <span className="item-card__quantity-display">
                                        {cartQuantity} {/*formatWithTax(food.price * cartQuantity, food.tax_rate)*/}
                                      </span>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleIncreaseQuantity(food.id, 'food')}
                                        className='item-card__remove-btn'
                                      >
                                        <Icon name="arriba" size={20} />
                                      </Button>
                                    </div>
                                    )}

                                    {/* Show add to cart button only if product is available */}
                                    {!isSoldOut && cartQuantity === 0 && (
                                    <Button
                                      className='item-card__agregar'
                                      variant="primary"
                                      size="sm"
                                      fullWidth
                                      onClick={() => handleAddToCart(food, 'food')}
                                      loading={addingToCart[itemId]}
                                      disabled={addingToCart[itemId]}
                                    >
                                      <Icon name="carrito" size={20} alt={t('cart.title')} />
                                      {addingToCart[itemId] ? t('cart.adding') : t('cart.addToCart')}
                                    </Button>
                                    )}

                                    {/* Show sold out message if product is not available */}
                                    {isSoldOut && (
                                      <div className="item-card__sold-out-message">
                                        <span className="item-card__sold-out-text">{t('stock.outOfStock')}</span>
                                      </div>
                                    )}

                                  {/* Stock Error Message */}
                                  {stockErrors[itemId] && (
                                    <div className="item-card__error">
                                      {stockErrors[itemId]}
                                    </div>
                                  )}
                                  </div>
                              </div>
                              </div>
                            </Card>
                          </>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Registration Encouragement */}
          {showRegistrationEncouragement && (
            <RegistrationEncouragement 
              variant="compact" 
              onSkip={() => setShowRegistrationEncouragement(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetails; 